import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useCallback, useMemo } from 'react';

import { fontStyles } from '@/styles/typography';

import { LiquidityProviderAvatar } from '@/ui/Avatars/LiquidityProviderAvatar';
import { Checkbox, type CheckedStates } from '@/ui/Checkbox';
import { Tooltip } from '@/ui/Tooltip';

import { classNames } from '@/utils/classnames';
import { applyRfqLikeSelectionRules } from '@/utils/liquiditySourcesValidation';

import type { SourceToRender } from '../index';

interface LiquiditySourcesTableProps {
  /** Sources to display, already filtered by the search query. */
  sources: SourceToRender[];
  /** Keys of the currently selected sources. */
  selectedKeys: string[];
  /** Keys of sources that cannot be toggled. */
  disabledKeys: string[];
  /** RFQ-like source keys, used to surface the disabled-source tooltip. */
  rfqLikeSourcesSet: Set<string>;
  /** All available source keys for the chain. */
  sourceKeys: string[];
  /** Stages a new set of selected source keys. */
  onSourcesSelected: (ids: string[]) => void;
}

/**
 * Live selection state and handlers passed to the (stable) column defs via the
 * table's `meta` so toggling never forces a full column rebuild.
 */
type LiquiditySourcesTableMeta = {
  selectedSet: Set<string>;
  disabledSet: Set<string>;
  rfqLikeSourcesSet: Set<string>;
  headerCheckedState: CheckedStates;
  toggleRow: (key: string) => void;
  toggleAll: (checked: boolean) => void;
};

const columnHelper = createColumnHelper<SourceToRender>();

const columns = [
  columnHelper.display({
    id: 'select',
    header: ({ table }) => {
      const { headerCheckedState, toggleAll } = table.options.meta as LiquiditySourcesTableMeta;
      return (
        <Checkbox
          checked={headerCheckedState}
          onCheckedChange={(checked) => toggleAll(checked === 'checked')}
        />
      );
    },
    cell: ({ row, table }) => {
      const { selectedSet, disabledSet, toggleRow } = table.options
        .meta as LiquiditySourcesTableMeta;
      const { key } = row.original;
      // Stop propagation so the checkbox (incl. keyboard Space/Enter) drives the
      // toggle without the row's onClick also firing and cancelling it out.
      return (
        <span className="flex items-center" onClick={(event) => event.stopPropagation()}>
          <Checkbox
            checked={selectedSet.has(key) ? 'checked' : 'unchecked'}
            disabled={disabledSet.has(key)}
            onCheckedChange={() => toggleRow(key)}
          />
        </span>
      );
    },
  }),
  columnHelper.display({
    id: 'name',
    header: 'Name',
    cell: ({ row, table }) => {
      const { disabledSet, rfqLikeSourcesSet } = table.options.meta as LiquiditySourcesTableMeta;
      const source = row.original;
      const isDisabled = disabledSet.has(source.key);
      const isDisabledRfqLikeSource = isDisabled && rfqLikeSourcesSet.has(source.key);
      const displayName = source.displayName === '0x' ? '0x RFQ' : source.displayName;

      return (
        <div className="flex items-center">
          <div className="flex items-center justify-center font-semibold overflow-hidden p-0 mr-2 w-6 h-6 rounded-sm">
            <LiquidityProviderAvatar name={source.key} size={20} />
          </div>
          <div className="flex items-center">
            {isDisabledRfqLikeSource ? (
              <Tooltip.Root>
                <Tooltip.Trigger asChild>
                  <p
                    className={fontStyles({
                      variant: 'BODY_MD',
                      class: 'text-token-gray-600 opacity-50 cursor-help',
                    })}
                  >
                    {displayName}
                  </p>
                </Tooltip.Trigger>
                <Tooltip.Portal>
                  <Tooltip.Content
                    side="top"
                    align="center"
                    className="whitespace-nowrap text-sm min-w-max"
                  >
                    All liquidity sources must be selected to enable RFQ liquidity
                  </Tooltip.Content>
                </Tooltip.Portal>
              </Tooltip.Root>
            ) : (
              <p
                className={fontStyles({
                  variant: 'BODY_MD',
                  class: isDisabled ? 'text-token-gray-600 opacity-50' : '',
                })}
              >
                {displayName}
              </p>
            )}
          </div>
        </div>
      );
    },
  }),
];

export function LiquiditySourcesTable({
  sources,
  selectedKeys,
  disabledKeys,
  rfqLikeSourcesSet,
  sourceKeys,
  onSourcesSelected,
}: LiquiditySourcesTableProps) {
  const toggleRow = useCallback(
    (key: string) => {
      if (disabledKeys.includes(key)) return;

      const isSelected = selectedKeys.includes(key);
      const next = new Set(selectedKeys);
      if (isSelected) {
        next.delete(key);
      } else {
        next.add(key);
      }

      // Selecting a non-RFQ source that completes the non-RFQ set re-enables and
      // re-selects the RFQ-like group, mirroring the prior behavior.
      if (!isSelected && !rfqLikeSourcesSet.has(key)) {
        const allNonRfqSelected = sourceKeys.every((k) => rfqLikeSourcesSet.has(k) || next.has(k));
        if (allNonRfqSelected) {
          for (const k of sourceKeys) {
            if (rfqLikeSourcesSet.has(k)) next.add(k);
          }
        }
      }

      onSourcesSelected(applyRfqLikeSelectionRules(selectedKeys, [...next], sourceKeys));
    },
    [selectedKeys, disabledKeys, rfqLikeSourcesSet, sourceKeys, onSourcesSelected],
  );

  const toggleAll = useCallback(
    (checked: boolean) => {
      if (checked) {
        onSourcesSelected(sourceKeys);
      } else {
        onSourcesSelected(applyRfqLikeSelectionRules(selectedKeys, [], sourceKeys));
      }
    },
    [selectedKeys, sourceKeys, onSourcesSelected],
  );

  const meta = useMemo<LiquiditySourcesTableMeta>(() => {
    const selectedSet = new Set(selectedKeys);
    const allSelected = sourceKeys.length > 0 && sourceKeys.every((key) => selectedSet.has(key));
    const someSelected = sourceKeys.some((key) => selectedSet.has(key));

    return {
      selectedSet,
      disabledSet: new Set(disabledKeys),
      rfqLikeSourcesSet,
      headerCheckedState: allSelected ? 'checked' : someSelected ? 'indeterminate' : 'unchecked',
      toggleRow,
      toggleAll,
    };
  }, [selectedKeys, disabledKeys, rfqLikeSourcesSet, sourceKeys, toggleRow, toggleAll]);

  const table = useReactTable({
    data: sources,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.key,
    meta,
  });

  return (
    <table className="w-full">
      <thead>
        {table.getHeaderGroups().map((headerGroup) => (
          <tr
            key={headerGroup.id}
            className="flex items-center gap-3 border-b border-b-special-border-divider bg-transparent my-1 py-2 px-2.5"
          >
            {headerGroup.headers.map((header) => (
              <th
                key={header.id}
                className={fontStyles({
                  variant: 'LABEL_SM',
                  class: 'text-token-gray-800 before:hidden after:hidden',
                })}
              >
                {header.isPlaceholder
                  ? null
                  : flexRender(header.column.columnDef.header, header.getContext())}
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody>
        {table.getRowModel().rows.map((row) => {
          const isDisabled = disabledKeys.includes(row.original.key);
          return (
            <tr
              key={row.id}
              onClick={() => toggleRow(row.original.key)}
              className={classNames(
                'flex items-center gap-3 bg-transparent py-2.5 px-2.5 rounded-md hover:bg-token-alphalight-200',
                isDisabled ? 'cursor-default' : 'cursor-pointer',
              )}
            >
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id} className="flex items-center">
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
